import { useNavigate } from "react-router-dom";
import { UserX } from "lucide-react";
import { formatDate, formatNumber, maskPhone, relativeTime } from "@/lib/utils";
import { useAuthStore } from "@/stores/authStore";
import { useListQuery } from "@/hooks/useListQuery";
import { DataTable, type Column } from "@/components/data/DataTable";
import {
  ActiveFilters,
  ExportButton,
  FilterSelect,
  PageHeader,
  SearchInput,
  Toolbar,
} from "@/components/data/toolbar";
import { EmptyState } from "@/components/ui/primitives";
import { RoleBadge, ScorePill, StatusBadge, type UserRow } from "./shared";

const FILTERS = ["role", "status", "city"] as const;

export const UsersListPage = () => {
  const navigate = useNavigate();
  const can = useAuthStore((s) => s.can);

  const list = useListQuery<UserRow>({
    key: "users",
    path: "/users",
    filterKeys: FILTERS,
    defaultSort: "created_at",
  });

  const columns: Column<UserRow>[] = [
    {
      key: "name",
      header: "User",
      sortable: true,
      render: (u) => (
        <div className="min-w-0">
          <p className="truncate font-medium">{u.name ?? "Unnamed"}</p>
          <p className="truncate text-xs text-muted-foreground">
            {u.email ?? maskPhone(u.phone)}
          </p>
        </div>
      ),
    },
    { key: "role", header: "Role", render: (u) => <RoleBadge role={u.role} /> },
    {
      key: "status",
      header: "Status",
      render: (u) => <StatusBadge status={u.status} />,
    },
    {
      key: "city",
      header: "City",
      hideOnMobile: true,
      render: (u) => u.city ?? <span className="text-muted-foreground">—</span>,
    },
    {
      key: "trust_score",
      header: "Trust",
      sortable: true,
      align: "right",
      hideOnMobile: true,
      render: (u) => <ScorePill score={u.trust_score} kind="trust" />,
    },
    {
      key: "risk_score",
      header: "Risk",
      sortable: true,
      align: "right",
      hideOnMobile: true,
      render: (u) =>
        u.risk_score > 0 ? (
          <ScorePill score={u.risk_score} kind="risk" />
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: "last_seen_at",
      header: "Last seen",
      sortable: true,
      hideOnMobile: true,
      render: (u) => (
        <span className="text-xs text-muted-foreground">
          {u.last_seen_at ? relativeTime(u.last_seen_at) : "never"}
        </span>
      ),
    },
    {
      key: "created_at",
      header: "Joined",
      sortable: true,
      align: "right",
      render: (u) => (
        <span className="text-xs text-muted-foreground">
          {formatDate(u.created_at)}
        </span>
      ),
    },
  ];

  const activeFilters = [
    list.q ? { key: "q", label: `“${list.q}”` } : null,
    list.filters.role ? { key: "role", label: `Role: ${list.filters.role}` } : null,
    list.filters.status
      ? { key: "status", label: `Status: ${list.filters.status}` }
      : null,
    list.filters.city ? { key: "city", label: `City: ${list.filters.city}` } : null,
  ].filter(Boolean) as { key: string; label: string }[];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Users"
        description="Every buyer and breeder on the platform."
        actions={
          can("users:export") && (
            <ExportButton
              path="/users/export"
              query={{ format: "xlsx", ...list.filters, q: list.q || undefined }}
              label="Export"
            />
          )
        }
      />

      <Toolbar>
        <SearchInput
          value={list.q}
          onChange={list.setQ}
          placeholder="Search name, email, phone, city…"
          className="w-full sm:w-80"
        />
        <FilterSelect
          label="Role"
          value={list.filters.role}
          onChange={(v) => list.setFilter("role", v)}
          options={[
            { value: "buyer", label: "Buyers" },
            { value: "breeder", label: "Breeders" },
          ]}
        />
        <FilterSelect
          label="Status"
          value={list.filters.status}
          onChange={(v) => list.setFilter("status", v)}
          options={[
            { value: "active", label: "Active" },
            { value: "suspended", label: "Suspended" },
            { value: "banned", label: "Banned" },
          ]}
        />
        <span className="ml-auto text-sm text-muted-foreground">
          {formatNumber(list.total)} total
        </span>
      </Toolbar>

      <ActiveFilters
        filters={activeFilters}
        onClear={(key) =>
          key === "q" ? list.setQ("") : list.setFilter(key, undefined)
        }
        onClearAll={list.clearFilters}
      />

      {list.error ? (
        <EmptyState
          icon={UserX}
          title="Could not load users"
          description={
            list.error instanceof Error ? list.error.message : "Something went wrong"
          }
        />
      ) : (
        <DataTable
          caption="All users, with role, status, trust and risk scores"
          columns={columns}
          rows={list.rows}
          rowKey={(u) => u.id}
          loading={list.loading}
          sort={list.sort}
          order={list.order}
          onSortChange={list.setSort}
          onRowClick={(u) => navigate(`/users/${u.id}`)}
          total={list.total}
          nextCursor={list.nextCursor}
          onLoadMore={list.loadMore}
          loadingMore={list.loadingMore}
          emptyTitle={
            list.activeFilterCount > 0 ? "No users match those filters" : "No users yet"
          }
          emptyDescription={
            list.activeFilterCount > 0
              ? "Try widening or clearing the filters."
              : undefined
          }
        />
      )}
    </div>
  );
};
